import { useQuery } from "@tanstack/react-query";
import { addDays, formatISO, startOfDay } from "date-fns";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

function money(v: unknown) {
  const n = typeof v === "number" ? v : Number(v ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

const OPEN_PO = ["draft", "sent", "confirmed", "partial"] as const;
const OPEN_SO = ["draft", "confirmed", "in_progress", "partial"] as const;

export type DashboardKpis = {
  stockWeight: number;
  qcHoldCount: number;
  openPoCount: number;
  openPoValue: number;
  openSoCount: number;
  openSoValue: number;
  overdueAr: number;
  outstandingAr: number;
  readyStages: number;
  dispatchOpen: number;
  partialPoCount: number;
};

export type UrgentSoRow = {
  id: string;
  so_number: string;
  client_name: string | null;
  due_date: string | null;
  status: string;
  grand_total: number;
};

export type OverdueInvoiceRow = {
  id: string;
  invoice_number: string;
  client_name: string | null;
  due_date: string;
  grand_total: number;
};

export type QcHoldRow = {
  id: string;
  batch_no: string;
  material_name: string | null;
  weight: number;
  quality_status: string;
};

export type DashboardData = {
  kpis: DashboardKpis;
  urgentSos: UrgentSoRow[];
  overdueInvoices: OverdueInvoiceRow[];
  qcHolds: QcHoldRow[];
};

export function useDashboard() {
  return useQuery({
    queryKey: ["dashboard_kpis"],
    enabled: isSupabaseConfigured,
    staleTime: 30_000,
    queryFn: async (): Promise<DashboardData> => {
      const today = formatISO(startOfDay(new Date()), { representation: "date" });
      const weekOut = formatISO(addDays(startOfDay(new Date()), 7), {
        representation: "date",
      });

      const [
        batchesRes,
        posRes,
        sosRes,
        invRes,
        clientsRes,
        stagesRes,
        dispatchRes,
      ] = await Promise.all([
        supabase
          .from("inventory_batches")
          .select("id, batch_no, weight, batch_status, quality_status, materials(name)")
          .eq("batch_status", "in_stock"),
        supabase
          .from("purchase_orders")
          .select("id, status, grand_total")
          .in("status", [...OPEN_PO]),
        supabase
          .from("sales_orders")
          .select("id, so_number, status, due_date, grand_total, clients(name)")
          .in("status", [...OPEN_SO]),
        supabase
          .from("invoices")
          .select("id, invoice_number, status, due_date, grand_total, clients(name)")
          .eq("status", "issued")
          .not("due_date", "is", null)
          .lt("due_date", today)
          .order("due_date", { ascending: true })
          .limit(12),
        supabase
          .from("clients")
          .select("outstanding_balance, overdue_balance")
          .eq("is_active", true),
        supabase
          .from("process_plan_stages")
          .select("id, status, process_plans!inner(id, status)")
          .eq("status", "planned"),
        supabase
          .from("dispatch_notes")
          .select("id, status")
          .in("status", ["draft", "loading"]),
      ]);

      for (const res of [
        batchesRes,
        posRes,
        sosRes,
        invRes,
        clientsRes,
        stagesRes,
        dispatchRes,
      ]) {
        if (res.error) throw res.error;
      }

      const batches = batchesRes.data ?? [];
      const stockWeight = batches.reduce((s, b) => s + money(b.weight), 0);
      const qcHoldCount = batches.filter((b) =>
        ["hold", "pending"].includes(String(b.quality_status)),
      ).length;

      const pos = posRes.data ?? [];
      const openPoCount = pos.length;
      const openPoValue = pos.reduce((s, p) => s + money(p.grand_total), 0);
      const partialPoCount = pos.filter((p) => p.status === "partial").length;

      const sos = sosRes.data ?? [];
      const openSoCount = sos.length;
      const openSoValue = sos.reduce((s, srow) => s + money(srow.grand_total), 0);

      const clients = clientsRes.data ?? [];
      const overdueAr = clients.reduce((s, c) => s + money(c.overdue_balance), 0);
      const outstandingAr = clients.reduce((s, c) => s + money(c.outstanding_balance), 0);

      const readyStages = (stagesRes.data ?? []).filter((row) => {
        const plan = (row as { process_plans?: { status?: string } }).process_plans;
        return plan && ["planned", "in_progress"].includes(String(plan.status));
      }).length;

      const dispatchOpen = (dispatchRes.data ?? []).length;

      const urgentSos: UrgentSoRow[] = sos
        .filter((s) => s.due_date && String(s.due_date) <= weekOut)
        .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)))
        .slice(0, 10)
        .map((s) => {
          const client = s.clients as { name?: string } | null;
          return {
            id: String(s.id),
            so_number: String(s.so_number),
            client_name: client?.name ?? null,
            due_date: (s.due_date as string | null) ?? null,
            status: String(s.status),
            grand_total: money(s.grand_total),
          };
        });

      const overdueInvoices: OverdueInvoiceRow[] = (invRes.data ?? []).map((inv) => {
        const client = inv.clients as { name?: string } | null;
        return {
          id: String(inv.id),
          invoice_number: String(inv.invoice_number),
          client_name: client?.name ?? null,
          due_date: String(inv.due_date),
          grand_total: money(inv.grand_total),
        };
      });

      const qcHolds: QcHoldRow[] = batches
        .filter((b) => String(b.quality_status) === "hold")
        .slice(0, 10)
        .map((b) => {
          const mat = b.materials as { name?: string } | null;
          return {
            id: String(b.id),
            batch_no: String(b.batch_no),
            material_name: mat?.name ?? null,
            weight: money(b.weight),
            quality_status: String(b.quality_status),
          };
        });

      return {
        kpis: {
          stockWeight,
          qcHoldCount,
          openPoCount,
          openPoValue,
          openSoCount,
          openSoValue,
          overdueAr,
          outstandingAr,
          readyStages,
          dispatchOpen,
          partialPoCount,
        },
        urgentSos,
        overdueInvoices,
        qcHolds,
      };
    },
  });
}
